/**
 * Community Partner attribution.
 *
 * Persists the `?cp=<token>` param so a partner gets credited for a
 * survey completion that may happen on a *different* host
 * (survey.haevn.co) days later. We:
 *
 *   1. On any pageload with `?cp=`, save the token to localStorage
 *      and fire a visit ping so the partner sees their reach grow.
 *   2. Expose `getCpToken()` so any "Start the survey" CTA can append
 *      the token to the outbound URL — keeping the attribution chain
 *      intact across the subdomain hop.
 *
 * The token is unguessable (72 bits, generated server-side) so simply
 * holding it does NOT grant any privilege; it's purely an attribution
 * key. We still treat it as PII-adjacent and never log it server-side
 * past the audit tables.
 */

const STORAGE_KEY = "haevn_cp";
const VISIT_DEDUPE_PREFIX = "haevn_cp_visit_";
// Public URL param name. The program tracking URL is
// `survey.haevn.co/start?ref=<partner_code>`, so `ref` is the canonical
// form. We keep `cp` as a back-compat fallback for any links that were
// generated in the v1 release before the rename.
const PARAM_NAMES = ["ref", "cp"];
const PARAM_NAME = "ref";

const safe = (fn, fallback = null) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export function getCpToken() {
  if (typeof window === "undefined") return "";
  return safe(() => localStorage.getItem(STORAGE_KEY) || "", "") || "";
}

function setCpToken(token) {
  safe(() => {
    if (token) localStorage.setItem(STORAGE_KEY, token);
  });
}

/**
 * Reads `?cp=` off the current URL. If present, stores it and pings
 * the backend visit log. Returns the active token (newly captured or
 * previously stored), or "" if none.
 *
 * Called on every route change from RouteTracker.
 */
export function captureCp(path) {
  if (typeof window === "undefined") return "";
  return (
    safe(() => {
      const params = new URLSearchParams(window.location.search);
      // Read whichever known param landed on the page.
      let incoming = "";
      for (const p of PARAM_NAMES) {
        const v = (params.get(p) || "").trim();
        if (v) { incoming = v; break; }
      }
      const existing = getCpToken();
      const token = incoming || existing;
      if (!token) return "";

      if (incoming && incoming !== existing) {
        setCpToken(incoming);
      }

      // Fire-and-forget visit ping, deduped per (token, path) per session
      // so SPA navs don't spam the audit log.
      const dedupeKey = `${VISIT_DEDUPE_PREFIX}${token}_${path || window.location.pathname}`;
      if (incoming || !sessionStorage.getItem(dedupeKey)) {
        sessionStorage.setItem(dedupeKey, "1");
        const body = {
          token,
          path: path || window.location.pathname,
          referrer: document.referrer || null,
          user_agent: (navigator.userAgent || "").slice(0, 500),
        };
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/community-partner/track/visit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => {});
      }
      return token;
    }, "") || ""
  );
}

/**
 * Returns the input URL with `?cp=<token>` (or `&cp=<token>`) appended
 * if a token is stored. Used by SurveyClickTracker to forward
 * attribution to survey.haevn.co at click time.
 */
export function appendCpToUrl(url) {
  const token = getCpToken();
  if (!token || !url) return url;
  // Don't overwrite a token the page already chose to set under any
  // recognized param name (ref or legacy cp).
  if (/[?&](ref|cp)=/.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${PARAM_NAME}=${encodeURIComponent(token)}`;
}
